<script setup lang="ts">
  import { computed } from 'vue';

  import type { UserListItem } from '@stuut/api-sdk/models';
  import { Alert, AlertDescription } from '@ui/components/alert';
  import { Button } from '@ui/components/button';

  export type UserOutOfOfficeAlertVariant = 'neutral' | 'warning';

  interface Props {
    summary?: string | null;
    user: UserListItem;
    /** `warning`: yellow (e.g. no backup). `neutral`: gray (e.g. backup assigned). */
    variant?: UserOutOfOfficeAlertVariant;
  }

  const props = withDefaults(defineProps<Props>(), {
    summary: null,
    variant: 'warning',
  });

  const emit = defineEmits<{
    backInOffice: [user: UserListItem];
    editOutOfOffice: [user: UserListItem];
  }>();

  const VARIANT_ICONS: Record<UserOutOfOfficeAlertVariant, string> = {
    neutral: 'far fa-circle-info',
    warning: 'far fa-triangle-exclamation',
  };

  const alertIcon = computed(() => VARIANT_ICONS[props.variant]);
</script>

<template>
  <Alert :variant="variant" :icon="alertIcon" class="w-full rounded-xs py-3">
    <div class="flex w-full min-w-0 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <AlertDescription v-if="summary" class="text-sm leading-5 text-muted-foreground">
        {{ summary }}
      </AlertDescription>

      <div class="flex shrink-0 gap-2 sm:ml-auto">
        <Button variant="outline" size="sm" type="button" @click="emit('backInOffice', user)">
          Back in office
        </Button>
        <Button variant="outline" size="sm" type="button" @click="emit('editOutOfOffice', user)">
          Edit
        </Button>
      </div>
    </div>
  </Alert>
</template>
